import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { formatTo12Hour } from "../../utils/formatTo12Hour";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";
import {
  X,
  Calendar,
  Clock,
  Search,
  RefreshCw,
  ChevronDown,
} from "lucide-react";

const CheckAttendancePage = () => {
  const [file, setFile] = useState(null);
  const [monthlyRecords, setMonthlyRecords] = useState(null);

  // Initialize with current year and month
  const getCurrentYearMonth = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
    return `${currentYear}-${currentMonth}`;
  };

  const [yearDay, setYearDay] = useState(getCurrentYearMonth());
  const [loading, setLoading] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [error, setError] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeRecords, setEmployeeRecords] = useState(null);
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const { roles } = useContext(AuthContext);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredData, setFilteredData] = useState(employeeRecords);
  const [tempFilters, setTempFilters] = useState({});

  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

  const filterableColumns = [
    // { field: "created_at", label: "Date" },
    { field: "employee_id", label: "EMP ID" },
    { field: "in_time", label: "In Time" },
    { field: "out_time", label: "Out Time" },
    { field: "total_hours", label: "Total Hours" },
    { field: "da_type", label: "Attendance Status" },
    { field: "portion", label: "Portion" },
  ];

  // Convert "20-05-2025" to "2025-05-20"
  function formatToInputDate(value) {
    if (!value) return "";
    const [day, month, year] = value.split("-");
    if (!day || !month || !year) return "";
    return `${year}-${month}-${day}`;
  }

  // Convert "2025-05-20" to "20-05-2025"
  function formatToDisplayDate(value) {
    if (!value) return "";
    const [year, month, day] = value.split("-");
    if (!day || !month || !year) return "";
    return `${day}-${month}-${year}`;
  }

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery("");
    setTempFilters({});
  };

  const clearFilterByKey = (key) => {
    const updatedFilters = { ...activeFilters };
    delete updatedFilters[key];
    setActiveFilters(updatedFilters);
  };

  useEffect(() => {
    if (showFilterMenu) {
      setTempFilters(activeFilters);
    }
  }, [showFilterMenu]);

  // Updated useEffect to fetch data when component mounts or yearDay changes
  useEffect(() => {
    if (token && yearDay) {
      fetchMonthlyAttendance();
    }
  }, [token, yearDay]);

  useEffect(() => {
    setFilteredData(monthlyRecords);
  }, [monthlyRecords]);

  // Apply filters and search when activeFilters or searchQuery changes
  useEffect(() => {
    if (!monthlyRecords) return;

    let filtered = [...monthlyRecords];

    // Apply search query
    if (searchQuery.trim()) {
      filtered = filtered.filter((record) =>
        Object.values(record).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      );
    }

    // Apply active filters
    Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue && filterValue.trim()) {
        filtered = filtered.filter((record) => {
          const recordValue = record[field];
          if (!recordValue) return false;
          return recordValue
            .toString()
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        });
      }
    });

    setFilteredData(filtered);
    setIsFiltering(
      searchQuery.trim() !== "" ||
        Object.values(activeFilters).some((val) => val && val.trim()),
    );
  }, [monthlyRecords, activeFilters, searchQuery]);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    const [, month] = yearDay.split("-");
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // adjust month if user picks current year but month is > current month
    const adjustedMonth =
      parseInt(newYear) === currentYear && parseInt(month) > currentMonth
        ? String(currentMonth).padStart(2, "0")
        : month;

    setYearDay(`${newYear}-${adjustedMonth}`);
  };

  // Handler to update month
  const handleDayChange = (e) => {
    const newMonth = e.target.value;
    const [year] = yearDay.split("-");
    setYearDay(`${year}-${newMonth}`);
  };

  const fetchMonthlyAttendance = async (page = 1) => {
    setLoadingEmployee(true);
    setError(null);

    try {
      const response = await axios.post(
        `https://hrmspayroll.prepstripe.com/api/attendance/monthly?page=${page}`,
        { period: yearDay },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setMonthlyRecords(response.data.data.data);

      setPagination({
        currentPage: response.data.data?.current_page || 1,
        totalPages: response.data.data?.last_page || 1,
        totalItems: response.data.data?.total || 0,
        perPage: response.data.data?.per_page || 30,
      });
    } catch (error) {
      console.error("Error fetching monthly attendance:", error);
      setError("Failed to fetch monthly attendance. Please try again.");
    } finally {
      setLoadingEmployee(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchMonthlyAttendance(newPage);
    }
  };

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">
                Check Attendance
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Get Monthly Attendance
        </h2>
      </div>

      {/* Filter Section */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6 w-full">
        {/* Search */}
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID, time, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-800 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-gray-400 shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex gap-4 w-full md:w-auto">
          {/* Year Selector */}
          <div className="relative flex-1 md:w-40 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <select
              value={yearDay.split("-")[0]}
              onChange={handleYearChange}
              className="w-full pl-3 pr-10 py-2.5 bg-transparent text-sm text-gray-800 dark:text-white appearance-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg cursor-pointer transition-all"
            >
              {Array.from({ length: 11 }, (_, i) => {
                const y = new Date().getFullYear() - 5 + i;
                return (
                  <option key={y} value={y}>
                    {y}
                  </option>
                );
              })}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>

          {/* Month Selector */}
          <div className="relative flex-1 md:w-40 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <select
              value={yearDay.split("-")[1]}
              onChange={handleDayChange}
              className="w-full pl-3 pr-10 py-2.5 bg-transparent text-sm text-gray-800 dark:text-white appearance-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg cursor-pointer transition-all"
            >
              {Array.from(
                {
                  length:
                    parseInt(yearDay.split("-")[0]) === new Date().getFullYear()
                      ? new Date().getMonth() + 1
                      : 12,
                },
                (_, i) => {
                  const m = String(i + 1).padStart(2, "0");
                  return (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  );
                },
              )}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Sr No.
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Employee ID
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date
                </div>
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  In Time
                </div>
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  Out Time
                </div>
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Total Hours
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Status
              </th>
              <th className="py-3 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">
                Portion
              </th>
            </tr>
          </thead>
          <tbody>
            {loading || loadingEmployee ? (
              <tr>
                <td colSpan="8" className="text-center py-8">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      Loading attendance...
                    </p>
                  </div>
                </td>
              </tr>
            ) : (isFiltering ? filteredData : monthlyRecords)?.length > 0 ? (
              (isFiltering ? filteredData : monthlyRecords).map(
                (record, index) => (
                  <tr
                    key={index}
                    className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white font-medium">
                      {index + 1}
                    </td>
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white font-medium">
                      {record.employee_id}
                    </td>
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                      {record.attendance_date}
                    </td>
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                      {record.in_time ? formatTo12Hour(record.in_time) : "-"}
                    </td>
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                      {record.out_time ? formatTo12Hour(record.out_time) : "-"}
                    </td>
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white font-semibold">
                      {record.total_hours}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          record.da_type === "A"
                            ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                            : record.da_type === "P"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                              : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400"
                        }`}
                      >
                        {record.da_type === "A"
                          ? "Absent"
                          : record.da_type === "P"
                            ? "Present"
                            : "Week Off"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs md:text-sm text-gray-700 dark:text-white">
                      {record.portion || "-"}
                    </td>
                  </tr>
                ),
              )
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-8">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      No attendance data available.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isFiltering &&
        monthlyRecords?.length > 0 &&
        pagination.totalPages > 1 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}
    </div>
  );
};

export default CheckAttendancePage;
